import React, { useState } from 'react';
import { Youtube, Link as LinkIcon, Info } from 'lucide-react';

interface YouTubeBlockProps {
  data: any;
  onChange: (data: any) => void;
}

const YouTubeBlock: React.FC<YouTubeBlockProps> = ({ data, onChange }) => {
  const [inputUrl, setInputUrl] = useState('');
  const [error, setError] = useState('');

  const extractYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleAddVideo = () => {
    if (!inputUrl.trim()) {
      setError('กรุณาใส่ลิงก์ YouTube');
      return;
    }

    const videoId = extractYouTubeId(inputUrl.trim());
    if (!videoId) {
      setError('ลิงก์ YouTube ไม่ถูกต้อง กรุณาลองใหม่');
      return;
    }

    onChange({
      videoId,
      url: inputUrl.trim(),
      embedUrl: `https://www.youtube.com/embed/${videoId}`
    });
    setInputUrl('');
    setError('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddVideo();
    }
  };

  if (!data?.videoId) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
        <div className="flex flex-col items-center">
          <Youtube className="w-16 h-16 text-red-500 mb-4" />
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-700">เพิ่มวิดีโอจาก YouTube</h3>
            <div className="relative">
              <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
              <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
                <p className="font-semibold mb-1">เพิ่มวิดีโอ YouTube</p>
                <p className="text-gray-300">แสดงวิดีโอจาก YouTube โดยวางลิงก์วิดีโอ เหมาะสำหรับรีวิวสินค้า วิธีใช้งาน หรือเนื้อหาต่างๆ</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-4 text-center">
            วางลิงก์ YouTube ของคุณที่นี่
          </p>
          
          <div className="w-full max-w-md">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={inputUrl}
                  onChange={(e) => {
                    setInputUrl(e.target.value);
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleAddVideo}
                className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                เพิ่ม
              </button>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-500">{error}</p>
            )}
            <p className="mt-3 text-xs text-gray-500">
              รองรับ: youtube.com/watch?v=xxx, youtu.be/xxx, หรือ ID วิดีโอ
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={data.embedUrl}
          className="absolute top-0 left-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video"
        />
      </div>
    </div>
  );
};

export default YouTubeBlock;
